import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { internalStaffLinks, invitations as seedInvitations, memberships as seedMemberships, organizations as seedOrganizations, orgRoleRegister, workspaceCatalogue } from '../data/organizations';
import { staffDirectory } from '../data/governance';
import { experienceById } from '../data/experiences';
import { activeMemberships, allMemberships, authorize, canEnterWorkspace, effectivePublicPermissions, internalStaffFor, requireInternal, resolveWorkspaces, type AuthorizationStore } from '../utils/authorization';
import { useAuth } from './AuthContext';
import type { AuthorizationAttempt, AuthorizationDecision, Invitation, Membership, Organization, PublicPermission, ResolvedWorkspace, ResourceRef } from '../types/workspace';
import type { StaffMember } from '../types/governance';

/**
 * The authorization layer, resolved for the authenticated identity.
 *
 * It sits between authentication and every product surface. Two things it
 * deliberately does NOT do:
 *
 *  - It does not store which workspaces an identity has. They are derived from
 *    memberships on every read, so revoking a membership closes its workspace
 *    immediately rather than at the next sign-in.
 *  - It does not accept a workspace, organization or role from the caller as
 *    evidence. `enterWorkspace` re-resolves and refuses; nothing here can be
 *    talked into granting access it did not already resolve.
 */
interface WorkspaceValue {
  /** Every membership, including the ones granting nothing — for review. */
  memberships: Membership[];
  /** Only the memberships currently granting something. */
  active: Membership[];
  organizations: Organization[];
  invitations: Invitation[];
  workspaces: ResolvedWorkspace[];
  activeWorkspace: ResolvedWorkspace | null;
  /** Refuses and records rather than throwing, so callers handle a decision. */
  enterWorkspace: (workspaceId: string) => AuthorizationDecision;
  can: (permission: PublicPermission, resource?: ResourceRef) => AuthorizationDecision;
  canEnter: (kind: string) => AuthorizationDecision;
  permissions: PublicPermission[];
  /** Non-null only for a provisioned internal staff link. */
  internalStaff: StaffMember | null;
  isInternal: boolean;
  internalDecision: AuthorizationDecision;
  /** Attempts, allowed and refused alike. Refusals are not silent. */
  attempts: AuthorizationAttempt[];
  record: (permission: string, resource: string, decision: AuthorizationDecision) => void;
  /** The one write: adds a public experience to this identity. */
  addExperience: (input: AddExperienceInput) => AddExperienceResult;
  store: AuthorizationStore;
}
export interface AddExperienceInput {
  experienceId: string;
  organizationName?: string;
  institutionId?: string | null;
  campusId?: string | null;
}
export type AddExperienceResult = {
  ok: true;
  membership: Membership;
  organizationId: string | null;
  pending: boolean;
} | {
  ok: false;
  error: string;
};
const WorkspaceContext = createContext<WorkspaceValue | null>(null);
export function WorkspaceProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    identity
  } = useAuth();
  const [attempts, setAttempts] = useState<AuthorizationAttempt[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const counter = useRef(0);

  /* Memberships and organizations are the only mutable registers here: adding
     an experience creates records, it never edits the identity. Roles, the
     permission register and the internal staff links stay immutable, so no
     signup path can invent a role or reach internal administration. */
  const [memberships, setMemberships] = useState<Membership[]>(seedMemberships);
  const [organizations, setOrganizations] = useState<Organization[]>(seedOrganizations);
  const store = useMemo<AuthorizationStore>(() => ({
    memberships,
    organizations,
    roles: orgRoleRegister,
    workspaces: workspaceCatalogue,
    internalLinks: internalStaffLinks,
    staff: staffDirectory
  }), [memberships, organizations]);

  /**
   * Add a public experience to the signed-in identity.
   *
   * This is the one write in the authorization layer, and it is deliberately
   * narrow: the role must exist in the immutable register, and it must be one
   * of the six public experiences. A caller naming any other role — including
   * anything internal — is refused rather than accommodated.
   */
  const addExperience = useCallback((input: AddExperienceInput): AddExperienceResult => {
    if (!identity) {
      return {
        ok: false,
        error: 'You need to be signed in to add an experience.'
      };
    }
    if (identity.status !== 'active' && identity.status !== 'pending_verification') {
      return {
        ok: false,
        error: 'This account cannot take on new roles right now.'
      };
    }
    const option = experienceById(input.experienceId);
    if (!option) return {
      ok: false,
      error: 'That is not a Campuzon experience.'
    };
    const role = orgRoleRegister.find(item => item.id === option.orgRoleId);
    if (!role) return {
      ok: false,
      error: 'That role is not in the register.'
    };
    counter.current += 1;
    const now = new Date().toISOString();
    let organizationId: string | null = null;

    /* Organization-owned workspaces get a real organization. The person is a
       member of it — they never become the organization. */
    if (option.organizationKind) {
      organizationId = `org-new-${counter.current}`;
      setOrganizations(current => [...current, {
        id: organizationId as string,
        kind: option.organizationKind as Organization['kind'],
        name: input.organizationName?.trim() || `${identity.displayName}’s organization`,
        legalName: null,
        tenantId: organizationId as string,
        institutionId: input.institutionId ?? null,
        campusIds: input.campusId ? [input.campusId] : [],
        status: 'onboarding',
        verification: {
          state: 'unverified',
          source: null,
          checkedAt: null,
          doesNotAssert: 'Campuzon has not confirmed that this organization is what it says it is.'
        },
        capabilities: option.id === 'housing' ? ['housing'] : [],
        createdAt: now,
        isDemo: false
      }]);
    }
    const membership: Membership = {
      id: `mem-new-${counter.current}`,
      identityId: identity.id,
      organizationId,
      orgRoleId: role.id,
      /* Where the experience needs a person to confirm it, the membership
         starts pending and therefore grants nothing — the workspace simply
         does not resolve yet. */
      status: option.startsActive ? 'active' : 'pending',
      scope: input.institutionId ? {
        level: 'institution',
        institutionId: input.institutionId
      } : {
        level: 'global'
      },
      invitedBy: null,
      invitedAt: null,
      joinedAt: option.startsActive ? now : null,
      endedAt: null,
      statusReason: option.startsActive ? null : 'Waiting on Campuzon to confirm you are authorised to act for this organization.'
    };
    setMemberships(current => [...current, membership]);
    return {
      ok: true,
      membership,
      organizationId,
      pending: !option.startsActive
    };
  }, [identity]);
  const workspaces = useMemo(() => resolveWorkspaces(store, identity), [store, identity]);
  const record = useCallback((permission: string, resource: string, decision: AuthorizationDecision) => {
    counter.current += 1;
    setAttempts(current => [{
      id: `att-${counter.current}`,
      at: new Date().toISOString(),
      identityId: identity?.id ?? null,
      permission,
      resource,
      decision
    }, ...current].slice(0, 100));
  }, [identity]);
  const can = useCallback((permission: PublicPermission, resource: ResourceRef = {}) => authorize(store, identity, permission, resource), [store, identity]);
  const canEnter = useCallback((kind: string) => canEnterWorkspace(store, identity, kind), [store, identity]);
  const enterWorkspace = useCallback((workspaceId: string): AuthorizationDecision => {
    /* The id is a claim. It is checked against freshly resolved access, so a
       forged or stale id selects nothing. */
    const match = workspaces.find(item => item.id === workspaceId);
    const decision: AuthorizationDecision = match ? {
      allowed: true,
      reason: null,
      viaMembership: match.membershipId,
      message: 'Allowed.'
    } : {
      allowed: false,
      reason: 'no_membership',
      viaMembership: null,
      message: 'You are not a member of anything that opens this workspace.'
    };
    record('workspace.enter', workspaceId, decision);
    if (decision.allowed && match) setActiveId(match.id);
    return decision;
  }, [workspaces, record]);
  const value = useMemo<WorkspaceValue>(() => {
    const internalStaff = internalStaffFor(store, identity);
    const active = workspaces.find(item => item.id === activeId) ?? workspaces[0] ?? null;
    return {
      memberships: identity ? allMemberships(store, identity.id) : [],
      active: identity ? activeMemberships(store, identity.id) : [],
      organizations: store.organizations,
      addExperience,
      invitations: seedInvitations,
      workspaces,
      activeWorkspace: active,
      enterWorkspace,
      can,
      canEnter,
      permissions: effectivePublicPermissions(store, identity),
      internalStaff,
      isInternal: internalStaff !== null,
      internalDecision: requireInternal(store, identity),
      attempts,
      record,
      store
    };
  }, [store, identity, workspaces, activeId, enterWorkspace, can, canEnter, attempts, record, addExperience]);
  return <WorkspaceContext.Provider value={value} data-id="element-5781" data-mp="3q1am1g8v206c">{children}</WorkspaceContext.Provider>;
}
export function useWorkspace(): WorkspaceValue {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used inside WorkspaceProvider');
  }
  return context;
}